import React, { useState } from 'react';
import { toast } from 'sonner';
import { Send, Mail, Phone, MapPin, CheckCircle, MessageCircle, Loader2, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n';

// Web3Forms public access key - designed to be embedded client-side (official pattern)
const WEB3FORMS_ACCESS_KEY = '014e901a-3992-4b3b-83e6-60065cd3960d';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const body = new FormData();
      body.append('access_key', WEB3FORMS_ACCESS_KEY);
      body.append('subject', `Fegga website - New message from ${formData.name}`);
      body.append('from_name', 'Fegga Website');
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('company', formData.company);
      body.append('country', formData.country);
      body.append('message', formData.message);
      const res = await fetch('https://api.web3forms.com/submit', { method: 'POST', body });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setIsSubmitted(true);
      toast.success(t.contact.toastSuccess);
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          country: '',
          message: ''
        });
      }, 4000);
    } catch (error) {
      const msg = error?.message && error.message !== 'Failed to fetch' ? `${t.contact.toastError} (${error.message})` : t.contact.toastError;
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-[#E9782A] font-semibold text-sm uppercase tracking-wider">
            {t.contact.label}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-4 mb-6">
            {t.contact.titlePre}{' '}
            <span className="text-[#E9782A]">{t.contact.titleHighlight}</span>
          </h2>
          <p className="text-xl text-gray-600">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="shadow-xl border-2 border-gray-100">
            <CardContent className="p-8">
              {isSubmitted ? (
                <div className="text-center py-12 animate-in fade-in zoom-in" data-testid="contact-success-message">
                  <CheckCircle className="text-[#E9782A] mx-auto mb-4" size={64} />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {t.contact.successTitle}
                  </h3>
                  <p className="text-gray-600">
                    {t.contact.successText}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold">
                      {t.contact.nameLabel}
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t.contact.namePlaceholder}
                      data-testid="contact-name-input"
                      className="mt-2 border-2 border-gray-200 focus:border-[#E9782A] transition-colors"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">
                      {t.contact.emailLabel}
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.contact.emailPlaceholder}
                      data-testid="contact-email-input"
                      className="mt-2 border-2 border-gray-200 focus:border-[#E9782A] transition-colors"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="company" className="text-gray-700 font-semibold">
                        {t.contact.companyLabel}
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder={t.contact.companyPlaceholder}
                        data-testid="contact-company-input"
                        className="mt-2 border-2 border-gray-200 focus:border-[#E9782A] transition-colors"
                      />
                    </div>

                    <div>
                      <Label htmlFor="country" className="text-gray-700 font-semibold">
                        {t.contact.countryLabel}
                      </Label>
                      <Input
                        id="country"
                        name="country"
                        type="text"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder={t.contact.countryPlaceholder}
                        data-testid="contact-country-input"
                        className="mt-2 border-2 border-gray-200 focus:border-[#E9782A] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-semibold">
                      {t.contact.messageLabel}
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t.contact.messagePlaceholder}
                      rows={6}
                      data-testid="contact-message-input"
                      className="mt-2 border-2 border-gray-200 focus:border-[#E9782A] transition-colors resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="w-full bg-[#E9782A] hover:bg-[#d96820] text-white py-6 text-lg font-semibold transition-all duration-200 hover:shadow-xl group"
                  >
                    {isSubmitting ? (
                      <>
                        {t.contact.sending}
                        <Loader2 className="ml-2 animate-spin" size={20} />
                      </>
                    ) : (
                      <>
                        {t.contact.submit}
                        <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-8">
            {/* Instagram Highlight Card */}
            <a
              href="https://www.instagram.com/fegga.agency?igsh=MXcxc2I2MzZieHlqeA=="
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-highlight-card"
              className="block rounded-2xl p-[2px] bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] shadow-xl hover:scale-[1.02] transition-transform duration-200"
            >
              <div className="bg-white rounded-2xl p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] flex items-center justify-center flex-shrink-0">
                    <Instagram className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{t.instagram.title}</h3>
                    <p className="text-gray-600 text-sm mb-2">{t.instagram.text}</p>
                    <span className="inline-flex items-center font-semibold text-sm bg-gradient-to-r from-[#d62976] to-[#4f5bd5] bg-clip-text text-transparent">
                      {t.instagram.cta} →
                    </span>
                  </div>
                </div>
              </div>
            </a>

            <Card className="border-2 border-gray-100 hover:border-[#E9782A]/20 transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E9782A]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#E9782A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{t.contact.emailTitle}</h3>
                    <p className="text-gray-600" data-testid="contact-info-email">feggaagency@gmail.com</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#E9782A]/20 transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E9782A]/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#E9782A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{t.contact.phoneTitle}</h3>
                    <p className="text-gray-600 flex items-center gap-2" data-testid="contact-info-phone-1">
                      +243 895 534 241
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        <MessageCircle size={12} /> WhatsApp
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-100 hover:border-[#E9782A]/20 transition-all duration-300 hover:shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E9782A]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#E9782A]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{t.contact.hqTitle}</h3>
                    <p className="text-gray-600" data-testid="contact-info-address">{t.contact.hqValue}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-[#E9782A] to-[#d96820] rounded-2xl p-8 text-white shadow-2xl">
              <h3 className="text-2xl font-bold mb-3">
                {t.contact.ctaTitle}
              </h3>
              <p className="mb-6 opacity-90">
                {t.contact.ctaText}
              </p>
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span className="font-semibold">{t.contact.online}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
